<template lang="pug">
  form.ads-contact-form(
    @submit="onSubmit"
    ref="form"
  )
    .ads-contact-form__success(
      v-if="formState === 'success'"
    )
      .ads-contact-form__success-title Спасибо!
      .ads-contact-form__success-description Мы ответим быстро, обещаем.
    .ads-contact-form__content(
      v-else
    )
      .ads-contact-form__title {{ title }}
      .ads-contact-form__field.ads-contact-form__field--name.field
        TextAreaCustom(
          v-if="isMobile"
          :id="fieldName.id",
          v-model="fieldName.value"
          :placeholder="fieldName.placeholder"
          :rows="fieldNameRows"
          @input="onInput"
        )

        TextInputCustom(
           v-else
           :id="fieldName.id",
           v-model="fieldName.value"
           :placeholder="fieldName.placeholder"
           @input="onInput"
        )
      .ads-contact-form__field.ads-contact-form__field--target.field
        TextAreaCustom(
          :id="fieldTarget.id",
          v-model="fieldTarget.value"
          :placeholder="fieldTarget.placeholder"
          :maxlength="fieldTarget.maxLength"
          :rows="2"
          @input="onInput"
        )
          .field__footer
            .field__counter
              span {{ parsedFieldNotTags(fieldTarget).length }}
              span / {{fieldTarget.maxLength}}

      .ads-contact-form__contact-title Как с вами связаться?
      .ads-contact-form__radio-group
        InputRadioGroup(
          name="contactType"
          v-model="contactType"
          :options="contactTypes"
          @input="onInput"
        )
      .ads-contact-form__contact
        TextInputCustom(
          ref="emailInput"
          v-show="contactType === 'email'"
          v-model="fieldEmail.value"
          :id="fieldEmail.id"
          :placeholder="fieldEmail.placeholder"
          :state="fieldEmail.state"
          @on-focus="onFocusField(fieldEmail)"
          @on-blur="onBlurField(fieldEmail)"
          @input="onInput"
        )
          .field__state
            .field__verify(v-if="fieldEmail.isFocus || fieldEmail.isVerify")
              .verify(
                :class="verifyEmailClass"
              )
        TextInputCustom(
          ref="phoneInput"
          v-show="contactType === 'phone'"
          v-model="fieldPhone.value"
          :id="fieldPhone.id"
          :placeholder="fieldPhone.placeholder"
          :state="fieldPhone.state"
          @on-focus="onFocusField(fieldPhone)"
          @on-blur="onBlurField(fieldPhone)"
          type="tel"
          :masked="fieldPhone.mask"
          @input="onInput"
        )
          .field__state
            .field__verify(v-if="fieldPhone.isFocus || fieldPhone.isVerify")
              .verify(
                :class="verifyPhoneClass"
              )

      .ads-contact-form__agreement
        InputCheckbox(
          id="agreement"
          v-model="agreement"
          @input="onInput"
        )
          div Я прочитал(а)#{' '}
            a(href='/terms/', target='_blank') Пользовательское соглашение
            span #{' '} и даю согласие на обработку своих персональных данных.

      .ads-contact-form__submit
        button.ads-contact-form__submit-button(
          type="submit"
          :disabled="disabledForm"
        ) Отправить
      .ads-contact-form__error(v-if="formError")
          SvgAsset(:content="errorIconContent")
          span {{formError}}
</template>

<script lang="ts">
  import type { PropOptions } from 'vue';
  import { isMobile } from 'mobile-device-detect';
  import TextAreaCustom from '@/components/TextArea/TextArea.vue';
  import InputRadioGroup from '@/components/InputRadio/InputRadioGroup.vue';
  import InputCheckbox from '@/components/InputCheckbox/InputCheckbox.vue';
  import TextInputCustom from '@/components/TextInput/TextInput.vue';
  import { validateEmail, validatePhoneByMask } from '~/utils';
  import WindowBreakpoints from '~/utils/windowBreakpoints';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import errorIconContent from '~/assets/images/error.svg?raw';

  export default defineNuxtComponent({
    name: 'AdsContactForm',

    components: {
      TextAreaCustom,
      InputRadioGroup,
      TextInputCustom,
      InputCheckbox,
      SvgAsset,
    },

    props: {
      elem: {
        type: Object,
        required: true,
      } as PropOptions<TContent>,
    },

    data: () => ({
      fieldName: {
        id: 'name',
        placeholder: 'Укажите реальное или вымышленное имя',
        value: '',
        propertyAPIName: 'display_name',
        ignoreTags: false,
        isFocus: false,
      },

      fieldTarget: {
        id: 'target',
        placeholder: 'Расскажите нам о своих целях',
        maxLength: 400,
        value: '',
        propertyAPIName: 'about',
        ignoreTags: true,
        isFocus: false,
      },

      fieldEmail: {
        id: 'email',
        placeholder: 'Введите почту',
        value: '',
        propertyAPIName: 'email',
        isVerify: false,
        validFunc: validateEmail,
        isFocus: false,
        state: 'pending',
      },

      fieldPhone: {
        id: 'phone',
        placeholder: '+7 (999) 999-99-99',
        value: '',
        propertyAPIName: 'phone',
        isVerify: false,
        validFunc: validatePhoneByMask,
        isFocus: false,
        state: 'pending',
        mask: '+7 (###) ###-##-##',
      },

      contactType: 'email',
      contactTypes: [
        {
          id: 'email',
          title: 'E-mail',
        },
        {
          id: 'phone',
          title: 'Мобильный',
        },
      ],

      agreement: false,
      formState: 'pending',
      formError: '',
      isEditFormStart: false,
      breakpoints: null as null | WindowBreakpoints,
    }),

    computed: {
      errorIconContent() {
        return errorIconContent;
      },
      isMobile(): boolean {
        return isMobile;
      },

      disabledForm(): any {
        const {
          agreement,
          contactType,
          fieldEmail,
          fieldPhone,
          fieldName,
          fieldTarget,
          formError,
        } = this;

        const validetaByContactType = {
          email: () => fieldEmail.validFunc(fieldEmail.value),
          phone: () => fieldPhone.validFunc(fieldPhone.value, fieldPhone.mask),
        } as Record<string, Function>;

        const validateFields = validetaByContactType[contactType]() ?? false;

        return !agreement || formError || !fieldTarget.value || !fieldName.value || !validateFields;
      },

      verifyEmailClass(): any {
        return {
          'verify--send': !this.fieldEmail.isVerify,
          'verify--complete': this.fieldEmail.isVerify,
        };
      },

      verifyPhoneClass(): any {
        return {
          'verify--send': !this.fieldPhone.isVerify,
          'verify--complete': this.fieldPhone.isVerify,
        };
      },

      isVerifyEmailShow() {
        const { isVerify, isFocus } = this.fieldEmail;

        return isVerify || isFocus;
      },

      breakpointCurrent(): any {
        return this.breakpoints ? this.breakpoints.breakpointCurrent : null;
      },

      fieldNameRows(): number {
        return this.breakpointCurrent === 'mobile' ? 2 : 1;
      },

      title(): string {
        return this.elem.attributes.title ?? 'Напишите нам';
      },
    },

    watch: {
      'fieldEmail.value'(value) {
        this.fieldEmail.isVerify = validateEmail(value);
      },
      'fieldEmail.isVerify'(value) {
        this.fieldEmail.state = value ? 'pending' : 'error';
      },
      'fieldEmail.isFocus'(value) {
        if (!value) {
          this.fieldEmail.isVerify = validateEmail(this.fieldEmail.value);
        }
      },

      'fieldPhone.value'(value) {
        this.fieldPhone.isVerify = validatePhoneByMask(value, this.fieldPhone.mask);
      },
      'fieldPhone.isVerify'(value) {
        this.fieldPhone.state = value ? 'pending' : 'error';
      },
      'fieldPhone.isFocus'(value) {
        if (!value) {
          this.fieldPhone.isVerify = validatePhoneByMask(
            this.fieldPhone.value,
            this.fieldPhone.mask,
          );
        }
      },

      contactType(value) {
        this.$nextTick(() => {
          const { phoneInput, emailInput } = this.$refs;
          const inputComponentRef = value === 'phone' ? phoneInput : emailInput;
          const inputRef = inputComponentRef.$refs.inputRef as HTMLInputElement;

          this.fieldEmail.value = '';
          this.fieldPhone.value = '';

          inputRef?.focus();
        });
      },
    },

    mounted() {
      this.breakpoints = new WindowBreakpoints();
    },

    methods: {
      parsedFieldNotTags(field: FieldEdit): string {
        if (field.ignoreTags) {
          const regex = /(<([^>]+)>)/gi;
          const parseField = field?.value?.replace(regex, '');

          return parseField || '';
        } else {
          return field.value || '';
        }
      },

      onFocusField(field: FieldEdit) {
        field.isFocus = true;
      },

      onBlurField(field: FieldEdit) {
        field.isFocus = false;
      },

      async onSubmit(event: Event): Promise<void> {
        event.preventDefault();

        const form = this.$refs.form as HTMLElement;

        try {
          const {
            fieldName: { value: name },
            fieldEmail: { value: email },
            fieldPhone: { value: phone },
            fieldTarget: { value: message },
          } = this;

          const { status, data } = await this.$ironMaidenApi.sendContactForm({
            name,
            message,
            phone,
            email,
          });

          if (status === 200) {
            this.formState = 'success';

            this.$nextTick(() => {
              form.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
            });
          } else {
            this.formError = data;
          }
        } catch (error) {
          // Стандартная заглушка если нет ответа от бэка
          this.formError =
            'Сообщение не отправилось по техническим причинам. Пожалуйста, отправьте свой запрос на почту mironycheva@lifehacker.ru';

          console.error(error);
        }
      },

      onInput(): void {
        this.isEditFormStart = true;
      },
    },
  });
</script>

<style lang="scss" scoped src="@/assets/scss/field.scss"></style>
<style lang="scss" scoped src="@/assets/scss/verify.scss"></style>
<style lang="scss">
  .ads-contact-form {
    & + br {
      content: '';
    }

    &__field {
      &--name .text-input__actions {
        display: none;
      }

      &--target {
        textarea {
          max-height: 328px;

          @include tablet {
            max-height: 160px;
          }
        }
      }
    }

    .text-input__field:focus-within {
      border: 1px solid #2962f9;
    }
  }
</style>
<style lang="scss" scoped>
  .ads-contact-form {
    background-color: #e1eafd;
    margin-left: -16px;
    margin-right: -16px;
    padding: 32px 16px;
    margin-bottom: 48px;

    @include tablet {
      margin-left: 0;
      margin-right: 0;
      padding: 40px;
      margin-bottom: 60px;
    }

    &__title {
      @include fontH2;
      margin-bottom: 24px;
    }

    &__field {
      & + & {
        margin-top: 16px;
      }

      &--target,
      &--name {
        &:focus-within {
          border: 1px solid #2962f9;
        }
      }

      &--name {
        @include hover {
          border: 1px solid #e7e7e7;
        }
        @include tablet {
          textarea {
            height: 48px;
          }
        }
      }
    }

    &__error {
      @include smallText;
      display: flex;
      gap: 11px;
      margin-top: 16px;
      color: #fd4b4b;

      @include tablet {
        margin-top: 24px;
      }

      svg {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
      }
    }

    &__contact-title {
      @include fontH4AccentNormalText;
      margin: 24px 0 8px;
    }

    &__contact {
      padding: 12px 0 32px;

      @include tablet {
        padding-bottom: 40px;
      }
    }

    &__radio-group {
      padding: 12px 0 8px;
    }

    .ads-contact-form__submit {
      margin-top: 32px;

      @include tablet {
        margin-top: 40px;
      }

      &-button {
        cursor: pointer;
        padding: 12px;
        background-color: #2962f9;
        @include fontTextBtn;
        color: #fff;
        text-align: center;
        width: 100%;
        outline: none;
        border: none;
        border-radius: 2px;

        @include hover {
          background-color: #264eb1;
        }

        &:disabled {
          background-color: #d4d7de;
          cursor: default;

          @include hover {
            background-color: #d4d7de;
          }
        }
      }
    }

    .ads-contact-form__success {
      &-title {
        @include fontH4AccentNormalText;
        font-size: 20px;
        line-height: 28px;
        margin-bottom: 8px;
      }

      &-description {
        @include fontTextBtn;
        font-weight: normal;
      }
    }
  }
</style>
